import type { AdminUsageSeatRow } from '@nadoutong/contracts';
import { usdMicrosFromCentsFallback } from '../components/formatters.ts';
import { centsToYuan, moneyYuan } from './money.ts';
import type { SheetCell, SheetSpec } from './types.ts';

/** Chinese headers for the admin usage (seat) sheet — always zh, per #178. */
export const USAGE_SHEET_HEADERS: readonly string[] = [
  '成员',
  '邮箱',
  '账号 ID',
  '状态',
  '组织单元',
  '主模型',
  '输入 Token',
  '输出 Token',
  '缓存写 Token',
  '缓存读 Token',
  '请求数',
  '成功请求',
  '错误请求',
  '平均每请求 Token',
  '费用（元）',
  '月度上限（元）',
];

export const USAGE_SHEET_NAME = '用量明细';

/**
 * Exact-path seat money. `yuanByMember` is 费用（元） already converted
 * (micros summed per rate, then priced). `limitRate` prices 月度上限（元）.
 */
export interface UsageSeatMoney {
  readonly yuanByMember: ReadonlyMap<string, number>;
  /** USD-priced rate of the last covered month. The cap is a USD budget. */
  readonly limitRate: number;
}

const SEAT_STATUS_LABEL: Readonly<Record<AdminUsageSeatRow['status'], string>> = {
  active: '正常',
  disabled: '已停用',
  deleted: '已移除',
};

/**
 * `money` as a number is the fast path: one rate for 费用 and 月度上限,
 * computed from the seat row the same way a single-rate export always did.
 */
export function assembleUsageSheet(
  rows: readonly AdminUsageSeatRow[],
  money: number | UsageSeatMoney,
): SheetSpec {
  return {
    name: USAGE_SHEET_NAME,
    rows: [USAGE_SHEET_HEADERS, ...rows.map((row) => usageRow(row, money))],
  };
}

export function seatCostMicros(row: AdminUsageSeatRow): number {
  return usdMicrosFromCentsFallback(row.costUsdMicros, row.costUsdCents);
}

/** Per-seat micros. A member that appears twice has its micros added. */
export function indexSeatCostMicros(rows: readonly AdminUsageSeatRow[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const row of rows) {
    const id = row.memberAccountId;
    map.set(id, (map.get(id) ?? 0) + seatCostMicros(row));
  }
  return map;
}

/**
 * Per-model dashboard reads must reproduce the unfiltered month.
 * A seat missing on one side counts as 0.
 */
export function usageModelSplitMatches(input: {
  readonly unfilteredSummaryMicros: number;
  readonly unfilteredSeats: ReadonlyMap<string, number>;
  readonly modelSummaryMicros: readonly number[];
  readonly modelSeats: readonly ReadonlyMap<string, number>[];
}): boolean {
  let summary = 0;
  for (const micros of input.modelSummaryMicros) summary += micros;
  if (summary !== input.unfilteredSummaryMicros) return false;
  const ids = new Set<string>(input.unfilteredSeats.keys());
  for (const seats of input.modelSeats) {
    for (const id of seats.keys()) ids.add(id);
  }
  for (const id of ids) {
    let micros = 0;
    for (const seats of input.modelSeats) micros += seats.get(id) ?? 0;
    if (micros !== (input.unfilteredSeats.get(id) ?? 0)) return false;
  }
  return true;
}

function usageRow(row: AdminUsageSeatRow, money: number | UsageSeatMoney): readonly SheetCell[] {
  const yuan =
    typeof money === 'number'
      ? moneyYuan(row.costUsdMicros, row.costUsdCents, money)
      : (money.yuanByMember.get(row.memberAccountId) ?? 0);
  const limitRate = typeof money === 'number' ? money : money.limitRate;
  return [
    row.memberName ?? row.email ?? row.memberAccountId,
    row.email ?? '',
    row.memberAccountId,
    SEAT_STATUS_LABEL[row.status],
    row.department ?? '',
    row.primaryModel ?? row.defaultModel ?? '',
    row.inputTokens,
    row.outputTokens,
    row.cacheWriteTokens,
    row.cacheReadTokens,
    row.requests,
    row.successRequests,
    row.errorRequests,
    row.averageTokensPerRequest,
    yuan,
    centsToYuan(row.monthlyLimitUsdCents, limitRate),
  ];
}
